<template>
  <!-- Checkbox & Radio - uses <fieldset class="form-fieldset"> & <legend class="form-fieldset__legend"> -->
  <fieldset
    v-bind="$attrs"
    v-if="type === 'checkbox' || type === 'radio'"
    class="form-fieldset"
    :class="{
      'has-error': !!error,
      'is-required': required,
      'has-warning': warning,
      'has-success': success,
    }"
  >
    <slot name="label">
      <legend
        v-if="label"
        class="form-fieldset__legend"
        :class="{'sr-only': !showLabel}"
        :qaid="qaid ? qaid + '__label' : null"
      >
        <span v-html="label"></span>
        <span v-if="required" class="text-red ml-25">
          *
          <span class="sr-only">(required)</span>
        </span>
      </legend>
    </slot>

    <div v-if="type === 'checkbox'" :class="inputGroupClasses">
      <!-- Checkbox -->
      <slot name="input">
        <label
          v-for="(multiItem, index) in multiItems"
          :key="index"
          class="checkbox"
          :class="labelClasses"
          :qaid="qaid"
        >
          <input
            :id="multiItem.value"
            ref="input"
            :aria-describedby="ariaDescribedBy || null"
            :aria-errormessage="error ? errorId : null"
            :aria-invalid="!!error"
            :type="type"
            :name="multiItem.name || name"
            :value="multiItem.value"
            :disabled="disabled"
            :checked="multiItem.checked"
            :qaid="`formGroup${multiItem.value}`"
            @change="emitChange"
          />
          <span class="control-indicator"></span>
          <component :is="multiItem.component" v-if="multiItem.component" />
          <pdl-link
            v-else-if="multiItem.linkText"
            :id="`form-group-${multiItem.label}-label`"
            :qaid="`form-group-${multiItem.label}-label`"
            :href="multiItem.linkText"
            target="_blank"
            inline
            >{{ multiItem.displayName }}
          </pdl-link>
          <template v-else>
            <span
              :id="`form-group-${multiItem.label}-label`"
              :qaid="`form-group-${multiItem.label}-label`"
              v-html="multiItem.displayName"
            >
            </span>
            <span v-if="required" class="text-red ml-25">
              *
              <span class="sr-only">(required)</span>
            </span>
          </template>
        </label>
      </slot>
    </div>

    <div v-else :class="inputGroupClasses">
      <!-- Radio -->
      <slot name="input">
        <label v-for="(multiItem, index) in multiItems" :key="index" class="radio" :class="labelClasses">
          <input
            :id="multiItem.value"
            :class="{'radio-button-unavailable': multiItem.isUnavailable}"
            ref="input"
            :aria-describedby="ariaDescribedBy || null"
            :aria-errormessage="error ? errorId : null"
            :aria-invalid="!!error"
            :type="type"
            :name="name"
            :value="multiItem.value"
            :disabled="disabled"
            :checked="multiItem.value === modelValue"
            :qaid="`formGroup${multiItem.value}`"
            @change="emitChange"
          />
          <span v-if="!isRadioButton" class="control-indicator"></span>
          <component :is="multiItem.component" v-if="multiItem.component" />
          <pdl-link
            v-else-if="multiItem.linkText"
            :qaid="`formGroup${multiItem.value}_label`"
            :href="multiItem.linkText"
            target="_blank"
            inline
            >{{ multiItem.displayName }}
          </pdl-link>
          <span
            v-else
            :class="{
              'relative grow text-sm p-1 text-center radio-button transition duration-300 ease-out': isRadioButton,
            }"
            :qaid="`formGroup${multiItem.value}_label`"
            v-html="multiItem.displayName"
          ></span>
        </label>
      </slot>
    </div>

    <!-- @slot Slot to display helper text. -->
    <slot v-if="helpText" name="form-help">
      <span :id="helpId" class="form-help" v-html="helpText"></span>
    </slot>

    <!-- @slot Slot to display validation errors. -->
    <slot v-if="error" name="form-errors">
      <span v-show="error" :id="errorId" role="alert" class="form-feedback is-invalid" v-html="error"></span>
    </slot>
  </fieldset>

  <!-- Input, Textarea & Select - uses <div class="form-group"> & <label class="form-label"> -->
  <div
    v-else
    v-bind="$attrs"
    class="form-group"
    :qaid="qaid"
    :class="{
      'has-error': error,
      'is-required': required,
      'has-warning': warning,
      'has-success': success,
    }"
  >
    <slot name="label">
      <label
        v-if="label"
        class="form-label"
        :for="name"
        :class="{'sr-only': !showLabel}"
        :qaid="qaid ? qaid + '__label' : null"
      >
        <span v-html="label"></span>
        <span v-if="required" class="text-red ml-25">
          *
          <span class="sr-only">(required)</span>
        </span>
      </label>
    </slot>

    <slot name="input">
      <!-- Select -->
      <div v-if="type === 'select'" class="select">
        <select
          v-if="isMultipleSelect"
          :id="name"
          v-model="inputValue"
          :qaid="qaid"
          :name="name"
          class="form-control"
          :disabled="disabled"
          multiple
          :size="size"
          :aria-describedby="ariaDescribedBy || null"
          :aria-errormessage="error ? errorId : null"
          :aria-invalid="!!error"
          @blur="emitBlur"
          @input="emitChange"
        >
          <option
            v-for="(multiItem, index) in multiItems"
            :id="`${name}-${index}-${multiItem.value}`"
            ref="input"
            :key="`${name}-${index}-${multiItem.value}`"
            :value="multiItem.value"
            :selected="value === multiItem.value"
            :disabled="multiItem.disabled"
            v-html="multiItem.displayName"
          ></option>
        </select>
        <select
          v-else
          :id="name"
          v-model="inputValue"
          :qaid="qaid"
          :name="name"
          class="form-control"
          :disabled="disabled"
          :aria-describedby="ariaDescribedBy || null"
          :aria-errormessage="error ? errorId : null"
          :aria-invalid="!!error"
          @input="emitChange"
        >
          <option
            v-for="(multiItem, index) in multiItems"
            :id="`${name}-${index}-${multiItem.value}`"
            ref="input"
            :key="`${name}-${index}-${multiItem.value}`"
            :value="multiItem.value"
            :selected="modelValue === multiItem.value"
            :disabled="multiItem.disabled"
            v-html="multiItem.displayName"
          ></option>
        </select>
      </div>

      <!-- Textarea -->
      <textarea
        v-else-if="type === 'textArea' || type === 'textarea'"
        :id="name"
        ref="input"
        class="form-control"
        v-bind="$attrs"
        :aria-describedby="ariaDescribedBy || null"
        :aria-errormessage="error ? errorId : null"
        :aria-invalid="!!error"
        :type="type"
        :name="name"
        :value="modelValue"
        :placeholder="placeholder"
        :rows="rows"
        :maxlength="maxlength"
        :disabled="disabled"
        @blur="emitBlur"
        @focus="emitFocus"
        @change="emitChange"
        @input="updateValue"
      ></textarea>

      <!-- Input -->
      <input
        v-else
        :id="name"
        :autocomplete="autocomplete"
        ref="input"
        class="form-control"
        v-bind="$attrs"
        :aria-describedby="ariaDescribedBy || null"
        :aria-errormessage="error ? errorId : null"
        :aria-invalid="!!error"
        :step="step"
        :type="type"
        :name="name"
        :value="modelValue"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :disabled="disabled"
        @keypress="checkInput"
        @keyup="emitKeyup"
        @keydown="emitKeydown"
        @blur="emitBlur"
        @focus="emitFocus"
        @change="emitChange"
        @input="updateValue"
      />
      <!-- @slot Slot for input controls such as custom autofill suggestions. -->
      <slot name="controls"></slot>
    </slot>

    <!-- @slot Slot to display helper text. -->
    <slot v-if="helpText && helpText !== error" name="form-help">
      <span :id="helpId" class="form-help" v-html="helpText"></span>
    </slot>

    <!-- @slot Slot to display validation errors. -->
    <slot v-if="error" name="form-errors">
      <span v-show="error" :id="errorId" role="alert" class="form-feedback is-invalid" v-html="error"></span>
    </slot>
  </div>
</template>

<script>
import {PdlLink} from '@pedal/pdl-link';

export default {
  name: 'FormGroup',
  components: {PdlLink},

  inheritAttrs: false,

  props: {
    autocomplete: {
      type: String,
      default: '',
      required: false,
    },
    allowedCharactersRegex: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      required: false,
      default: '',
    },
    helpText: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelClasses: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: 250,
    },
    multiItems: {
      type: Array,
      default: () => [],
    },
    name: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    qaid: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 4,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    step: {
      type: Number,
      default: null,
    },
    type: {
      type: String,
      default: 'text',
      validator: (val) => {
        return (
          [
            'url',
            'text',
            'password',
            'email',
            'search',
            'number',
            'tel',
            'textArea',
            'textarea',
            'select',
            'checkbox',
            'radio',
          ].indexOf(val) !== -1
        );
      },
    },
    modelValue: {
      type: null,
      default: null,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    isMultipleSelect: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 0,
    },
    /**
     * Displays variant of inputs that are styled differently.
     * @type {String}
     * @default ''
     */
    variant: {
      type: String,
      default: '',
      validator: (val) => {
        return ['radioButton', ''].indexOf(val) !== -1;
      },
    },
    /**
     * Classes to be applied to the radio input container.
     * @type {String}
     * @default ''
     * @example
     * inputGroupClasses: 'grid gap-2 grid-cols-2'
     */
    inputGroupClasses: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      inputValue: this.isMultipleSelect ? [] : '',
    };
  },

  emits: ['update:modelValue'],

  computed: {
    helpId() {
      return `${this.name}-help`;
    },
    errorId() {
      return `${this.name}-error`;
    },
    handle() {
      return `${this.name}`;
    },
    isRadioButton() {
      return this.type === 'radio' && this.variant === 'radioButton';
    },
    ariaDescribedBy() {
      return [this.helpText && this.helpId, this.error && this.errorId].filter(Boolean).join(' ');
    },
  },

  watch: {
    inputValue(value) {
      this.$emit('update:modelValue', value);
    },
    modelValue(value) {
      if (value !== this.inputValue) {
        this.inputValue = value;
      }
    },
    multiItems(items) {
      if (!items || !items.length || !this.inputValue) {
        return;
      }

      const hasItem = items.findIndex((item) => item.value === this.inputValue) > -1;
      if (!hasItem) {
        const itemsWithValues = items.filter((item) => item.value && item.value.length);
        if (itemsWithValues.length) {
          this.inputValue = itemsWithValues[0].value;
        }
      }
    },
  },
  mounted() {
    if (this.$refs.input && this.$refs.input.value) {
      this.$refs.input.value = this.modelValue;
    }

    if (this.modelValue) {
      this.inputValue = this.modelValue;
    }
  },

  methods: {
    checkInput(e) {
      if (this.allowedCharactersRegex) {
        const regex = RegExp(this.allowedCharactersRegex);
        if (regex.test(String.fromCharCode(e.charCode))) {
          return true;
        } else {
          e.preventDefault();
        }
      }
    },

    emitKeyup(e) {
      this.$emit('keyup', e);
    },

    emitKeydown(e) {
      this.$emit('keydown', e);
    },

    emitBlur(e) {
      this.$emit('blur', e);
    },

    emitFocus(e) {
      this.$emit('focus', e);
    },
    emitChange(e) {
      this.$emit('update:modelValue', e.target.value);
      this.$emit('change', e);
    },
    updateValue(e) {
      this.$emit('update:modelValue', e.target.value);
    },
  },
};
</script>
<style lang="scss">
@import '_forms.scss';
</style>
